import './App.css';
import Store from './Redux';
import { lazy, Suspense, useEffect } from 'react';
import { Provider } from 'react-redux';
import { useDispatch } from 'react-redux';
import { Route, Routes } from 'react-router';
import Guard, { AdminGuard, BlockAdminGuard } from './Routing/Guard';

import useLenis from './Hooks/useLenis';
import useLanguageParam from './Hooks/useLanguageParam';
import useRenderLog from './Hooks/useRenderLog';

import * as authActions from './Redux/Auth/AuthActions';
import * as cartActions from './Redux/Cart/CartActions';

import useUtmCapture, { useUtmPreserve } from './Hooks/useUtmCapture';
import Toast from './Utils/Toast/toast-component';
import PopUpPromotion from './Utils/Modal/pop-up-modal-component';
import ThemeProvider from './Utils/Theme/theme-provider-component';

// The landing screen is the first paint — keep it in the main bundle.
import Landing from './Screens/Landing-screen/Landing';
// ClearViolations Florida public home (money page) — first paint, main bundle.
import HomeScreen from './Screens/Home-Screen/home-screen';

// Everything else is code-split: each route's chunk downloads on first visit.
// This keeps the initial main.js small (it used to ship all screens, admin
// dashboard included, to every visitor).
const Login = lazy(() => import('./Screens/Login-Screen/Login'));
const SignUp = lazy(() => import('./Screens/SignUp-Screen/SignUp'));
const ResetPassword = lazy(() => import('./Screens/ResetPassword-Screen/ResetPassword'));
const AboutScreen = lazy(() => import('./Screens/AboutUs-screren/About-screen'));
const TeamMemberScreen = lazy(() => import('./Screens/TeamMember-screen/TeamMember-screen'));
const ContactScreen = lazy(() => import('./Screens/Contact-screen/Contact-Screen'));
const PromotionScreen = lazy(() => import('./Screens/Promotion-Screen/Promotion-Screen'));
const OurWorkScreen = lazy(() => import('./Screens/OurWork-screen/OurWorkScreen'));
const ServicesScreen = lazy(() => import('./Screens/Services-screen/Services-Screen'));
const ServicesCategory = lazy(() => import('./Screens/Services-screen/ServicesCategory'));
const BlogScreen = lazy(() => import('./Screens/Blog-screen/BlogScreen'));
const BlogPost = lazy(() => import('./Screens/Blog-screen/BlogPost'));
const Service = lazy(() => import('./Screens/Services-screen/Service'));
const EventsScreen = lazy(() => import('./Screens/Events-screen/EventsScreen'));
const EventPost = lazy(() => import('./Screens/Events-screen/EventPost'));
const CommunityJoin = lazy(() => import('./Screens/Community-screen/community-join-screen'));
const Donation = lazy(() => import('./Screens/Donation-screen/Donation'));
const SubscriptionsScreen = lazy(() => import('./Screens/Subscriptions-screen/Subscriptions-Screen'));
const SubscriptionPost = lazy(() => import('./Screens/Subscriptions-screen/SubscriptionPost'));
const Terms = lazy(() => import('./Screens/Terms-screen/Terms'));
const Privacy = lazy(() => import('./Screens/Privacy-screen/Privacy'));
const CategoriesScreen = lazy(() => import('./Screens/Category-screen/Categories-screen'));
const CategoryScreen = lazy(() => import('./Screens/Category-screen/Category-screen'));
const ThankYou = lazy(() => import('./Screens/ThankYou-screen/ThankYou'));
const NotFound = lazy(() => import('./Screens/Error-Screen/NotFound-screen'));
const PaymentSuccess = lazy(() => import('./Screens/Success-screen/Payment-Success'));
const OrderTracker = lazy(() => import('./Screens/Success-screen/OrderTracker'));

// Admin — never shipped to public visitors anymore.
const Admin = lazy(() => import('./Screens/Admin-Screen/Admin'));
const Editor = lazy(() => import('./Screens/Editor-Screen/Editor-screen'));
const EventsEditor = lazy(() => import('./Screens/Events-Editor-Screen/EventsEditor'));
const ProductEditor = lazy(() => import('./Screens/Editor-Screen/Product-Editor-screen'));
const FlowmaGenerate = lazy(() => import('./Screens/FlowmaGenerate-Screen/FlowmaGenerate'));

function AppContent() {
  useRenderLog('AppContent');
  const dispatch = useDispatch();
  useLanguageParam();
  useLenis();
  useUtmCapture();
  useUtmPreserve();

  useEffect(() => {
    dispatch(authActions.isLoggedIn());
    // All landing content (business info, banners, links, categories, services) is
    // loaded in one call by useLanguageParam's loadBootstrap above.
  }, [dispatch]);

  return (
    <>
      <div className="App font-primary">
        <ThemeProvider />
        <Toast />
        <PopUpPromotion />
        <Suspense fallback={<div className="min-h-screen bg-primary-950" />}>
        <Routes>

          <Route path="/" element={<Landing />} />
          {/* Standalone ClearViolations home kept for reference (superseded by Landing): */}
          {/* <Route path="/" element={<HomeScreen />} /> */}
          <Route path="/home-cv" element={<HomeScreen />} />

          <Route path="/landing" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/resetpswd" element={<ResetPassword />} />
          <Route path="/signup" element={<SignUp />} />
 
          <Route path="/about" element={<AboutScreen />} />
          {/* <Route path="/partners" element={<PartnersScreen />} /> */}
          <Route path="/team/:slug" element={<TeamMemberScreen />} />
          <Route path="/contact" element={<ContactScreen />} />
          <Route path="/promotion" element={<PromotionScreen />} />
          <Route path="/our-work" element={<OurWorkScreen />} />
 
          {/* <Route path="/porfolio" element={<WebsitesShowcase />} /> */}
 
          {/* <Route path="/projects/:projectSlug" element={<Project />} /> */}

          {/* <Route path="/locations/" element={<CommingSoon />} /> */}
          {/* <Route path="/locations/:locationSlug" element={<CommingSoon />} /> */}

          <Route path="/services/" element={<ServicesScreen />} />
          {/* Categories are 1-1 with services for now — services grid links straight to /services/:slug */}
          {/* <Route path="/services/category/:categorySlug" element={<ServicesCategory />} /> */}
          <Route path="/services/:service" element={<Service />} />

          <Route path="/blog" element={<BlogScreen />} />
          <Route path="/blog/:blogSlug" element={<BlogPost />} />

          <Route path="/events" element={<EventsScreen />} />
          <Route path="/events/:eventSlug" element={<EventPost />} />

          <Route path="/community/join" element={<CommunityJoin />} />
          <Route path="/donate" element={<Donation />} />
          <Route path="/subscriptions" element={<SubscriptionsScreen />} />
          <Route path="/subscriptions/:subscriptionSlug" element={<SubscriptionPost />} />


          <Route path="/terms" element={<Terms />} />
          {/* <Route path="/terms-hosting" element={<TermsHosting />} /> */}

          <Route path="/privacy" element={<Privacy />} />

          {/* <Route path="/shipping" element={<Shipping />} /> */}
          {/* <Route path="/refund-policy" element={<Refund />} /> */}
          {/* <Route path="/warranty" element={<Warranty />} /> */}
          {/* <Route path="/form-success" element={<Success />} /> */}

          {/* <Route path="/on-boarding" element={<FlowmaQuiz />} /> */}
          {/* <Route path="/flowma-for-business" element={<FlowmaBusinessScreen />} /> */}
          {/* <Route path="/on-boarding-website" element={<OnBoardingForm />} /> */}

          {/* <Route path="/shop" element={<Landing />} /> */}
          {/* <Route path="/shop/:productId" element={<Product />} /> */}

          <Route path="/categories/" element={<CategoriesScreen />} />
          <Route path="/categories/:slug" element={<CategoryScreen />} />

          {/* <Route path="/cart" element={<CartScreen />} /> */}
          {/* <Route path="/payment-failed" element={<PaymentFailed />} /> */}

          <Route path="/thank-you" element={<ThankYou />} />
          <Route path='/*' element={<NotFound />} />


          {/* <Route path="/poll" element={<Poll />} /> */}
          <Route element={<BlockAdminGuard />}>
            <Route path="/success" element={<PaymentSuccess />} />
          </Route>
          <Route path="/track" element={<OrderTracker />} />


          {/* ── Admin-only routes ── */}
          <Route element={<AdminGuard />}>
            <Route path="/admin" element={<Admin />} />
            <Route path="/home" element={<Admin />} />
            <Route path="/system-editor/:Field" element={<Editor />} />
            <Route path="/admin/events" element={<EventsEditor />} />
            <Route path="/product-editor" element={<ProductEditor />} />
            <Route path="/admin/generate" element={<FlowmaGenerate />} />
            {/* <Route path="/admin/order/:orderId" element={<AdminOrderDetail />} /> */}
          </Route>

          {/* ── Customer-only routes ── */}
          <Route element={<Guard />}>
            {/* <Route path="/home" element={<Home />} /> */}
            {/* <Route path="/account" element={<AccountScreen />} /> */}
          </Route>
        </Routes>
        </Suspense>
      </div >
    </>
  );
}

function App() {
  useEffect(() => {
    // window.Cookiebot.show();

  }, []);

  return (
    <Provider store={Store}>
      <AppContent />
    </Provider>
  );
}

export default App;
